import React, { useState, useEffect, FC } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Button, 
  Select, 
  DatePicker, 
  Input, 
  Tag, 
  Tooltip, 
  Modal, 
  Form, 
  message,
  Space,
  Badge,
  Drawer,
  Divider,
  Typography,
  Row,
  Col,
  Checkbox
} from "antd";
import { 
  EyeOutlined, 
  CheckOutlined, 
  CloseOutlined,
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
  ClearOutlined,
  SaveOutlined,
  ExportOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import { 
  mockPendingWorkOrdersData, 
  mockSiteOptions, 
  mockProjectOptions,
  mockContractTypeOptions 
} from "./mockData/pendingWorkOrdersData";
import { PendingWorkOrderData, ApprovalStatus, UserRole } from "./types";
import WorkOrderAside from "./workOrderAside";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const PendingWorkOrderApprovals: FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PendingWorkOrderData[]>([]);
  const [filteredData, setFilteredData] = useState<PendingWorkOrderData[]>([]);
  const [currentUserRole] = useState<UserRole>(UserRole.Checker);
  const [selectedKPIFilter, setSelectedKPIFilter] = useState<string>("status");
  
  // Filter states
  const [filters, setFilters] = useState({
    site: "",
    project: "",
    status: "",
    contractType: "",
    woOwner: "",
    valueRange: { min: "", max: "" },
    dateRange: null as any,
    globalSearch: ""
  });
  
  // Drawer states
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [savedFilters, setSavedFilters] = useState<any[]>([]);

  // Modal states
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [isReturnModalVisible, setIsReturnModalVisible] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<PendingWorkOrderData | null>(null);
  const [approvalForm] = Form.useForm();
  const [returnForm] = Form.useForm();

  useEffect(() => {
    loadPendingWorkOrders();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, data]);

  const loadPendingWorkOrders = async () => {
    try {
      setLoading(true);
      const roleBasedData = mockPendingWorkOrdersData.filter(wo => {
        if (currentUserRole === UserRole.Checker) {
          return wo.status === ApprovalStatus.SubmittedForApproval;
        } else if (currentUserRole === UserRole.Verifier) {
          return wo.status === ApprovalStatus.Checked;
        } else if (currentUserRole === UserRole.Approver) {
          return wo.status === ApprovalStatus.Verified;
        }
        return false;
      });
      
      setData(roleBasedData);
    } catch (error) {
      console.error("Error loading pending work orders:", error);
      message.error("Failed to load pending work orders");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...data];

    if (filters.globalSearch) {
      const searchTerm = filters.globalSearch.toLowerCase();
      filtered = filtered.filter((wo:any) => 
        wo.workOrderNumber.toLowerCase().includes(searchTerm) ||
        wo.workOrderTitle.toLowerCase().includes(searchTerm) ||
        wo.contractorName.toLowerCase().includes(searchTerm) ||
        wo.siteName.toLowerCase().includes(searchTerm) ||
        wo.projectName.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.site) {
      filtered = filtered.filter(wo => wo.siteId === filters.site);
    }
    if (filters.project) {
      filtered = filtered.filter(wo => wo.projectId === filters.project);
    }
    if (filters.status) {
      filtered = filtered.filter(wo => wo.status === filters.status);
    }
    if (filters.contractType) {
      filtered = filtered.filter(wo => wo.contractType === filters.contractType);
    }
    if (filters.woOwner) {
      filtered = filtered.filter(wo => 
        wo.workOrderOwner.toLowerCase().includes(filters.woOwner.toLowerCase())
      );
    }
    if (filters.valueRange.min || filters.valueRange.max) {
      filtered = filtered.filter(wo => {
        const value = wo.grossValue;
        const min = filters.valueRange.min ? parseFloat(filters.valueRange.min) : 0;
        const max = filters.valueRange.max ? parseFloat(filters.valueRange.max) : Infinity;
        return value >= min && value <= max;
      });
    }
    if (filters?.dateRange?.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      filtered = filtered.filter(wo => {
        const createdDate = dayjs(wo.createdAt);
        return createdDate.isAfter(startDate) && createdDate.isBefore(endDate);
      });
    }

    setFilteredData(filtered);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      site: "",
      project: "",
      status: "",
      contractType: "",
      woOwner: "",
      valueRange: { min: "", max: "" },
      dateRange: null,
      globalSearch: ""
    });
  };
  
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.site) count++;
    if (filters.project) count++;
    if (filters.status) count++;
    if (filters.contractType) count++;
    if (filters.woOwner) count++;
    if (filters.valueRange.min || filters.valueRange.max) count++;
    if (filters.dateRange?.length === 2) count++;
    if (filters.globalSearch) count++;
    return count;
  };
  
  const saveCurrentFilter = () => {
    const filterName = `Filter ${savedFilters.length + 1}`;
    const newFilter = {
      id: Date.now(),
      name: filterName,
      filters: { ...filters },
      createdAt: new Date().toISOString()
    };
    setSavedFilters(prev => [...prev, newFilter]);
    message.success('Filter saved successfully');
  };
  
  const applySavedFilter = (savedFilter: any) => {
    setFilters(savedFilter.filters);
    message.success(`Applied saved filter: ${savedFilter.name}`);
  };
  
  const deleteSavedFilter = (filterId: number) => {
    setSavedFilters(prev => prev.filter(f => f.id !== filterId));
    message.success('Filter deleted successfully');
  };
  
  // KPI Filter Categories
  const kpiFilterOptions = [
    { label: "By Status", value: "status" },
    { label: "By Value", value: "value" },
    { label: "By Age", value: "age" }
  ];

  // KPI Filter Change Handler
  const handleKPIFilterChange = (filterValue: string) => {
    setSelectedKPIFilter(filterValue);
  };

  // Dynamic Widget Data Generator
  const getWidgetsByFilter = (filterType: string) => {
    const totalPending = filteredData.length;
    const overdue = filteredData.filter(wo => calculateAge(wo.lastActionDate) > 3).length;
    const totalValue = filteredData.reduce((sum, wo) => sum + wo.grossValue, 0);
    const avgAge = totalPending > 0 ? Math.round(filteredData.reduce((sum, wo) => sum + calculateAge(wo.lastActionDate), 0) / totalPending) : 0;

    switch (filterType) {
      case "status":
        const submittedCount = filteredData.filter(wo => wo.status === ApprovalStatus.SubmittedForApproval).length;
        const checkedCount = filteredData.filter(wo => wo.status === ApprovalStatus.Checked).length;
        const verifiedCount = filteredData.filter(wo => wo.status === ApprovalStatus.Verified).length;
        
        return [
          {
            title: "Total Pending",
            key: "total",
            color: "#495057",
            percentage: "100",
            value: `${totalPending}`,
            icon: "📊"
          },
          // {
          //   title: "Submitted for Approval",
          //   key: "submitted",
          //   color: "#FD7E14",
          //   percentage: totalPending > 0 ? `${Math.round((submittedCount / totalPending) * 100)}` : "0",
          //   value: `${submittedCount}`,
          //   icon: "📝"
          // },
          {
            title: "Checked",
            key: "checked",
            color: "#007BFF",
            percentage: totalPending > 0 ? `${Math.round((checkedCount / totalPending) * 100)}` : "0",
            value: `${checkedCount}`,
            icon: "✅"
          },
          {
            title: "Verified",
            key: "verified",
            color: "#17A2B8",
            percentage: totalPending > 0 ? `${Math.round((verifiedCount / totalPending) * 100)}` : "0",
            value: `${verifiedCount}`,
            icon: "🔍"
          },
          {
            title: "Overdue (>3 days)",
            key: "overdue",
            color: "#DC3545",
            percentage: totalPending > 0 ? `${Math.round((overdue / totalPending) * 100)}` : "0",
            value: `${overdue}`,
            icon: "⚠️"
          }
        ];

      case "value":
        const highValue = filteredData.filter(wo => wo.grossValue > 1000000).length;
        const mediumValue = filteredData.filter(wo => wo.grossValue > 500000 && wo.grossValue <= 1000000).length;
        const lowValue = filteredData.filter(wo => wo.grossValue <= 500000).length;
        
        return [
          {
            title: "Total Value",
            key: "totalValue",
            color: "#495057",
            percentage: "100",
            value: new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(totalValue),
            icon: "💰"
          },
          {
            title: "High Value (>₹10L)",
            key: "highValue",
            color: "#DC3545",
            percentage: totalPending > 0 ? `${Math.round((highValue / totalPending) * 100)}` : "0",
            value: `${highValue}`,
            icon: "💎"
          },
          {
            title: "Medium Value (₹5L-₹10L)",
            key: "mediumValue",
            color: "#FFC107",
            percentage: totalPending > 0 ? `${Math.round((mediumValue / totalPending) * 100)}` : "0",
            value: `${mediumValue}`,
            icon: "💼"
          },
          {
            title: "Low Value (<₹5L)",
            key: "lowValue",
            color: "#198754",
            percentage: totalPending > 0 ? `${Math.round((lowValue / totalPending) * 100)}` : "0",
            value: `${lowValue}`,
            icon: "💵"
          }
        ];

      case "age":
        const fresh = filteredData.filter(wo => calculateAge(wo.lastActionDate) <= 1).length;
        const recent = filteredData.filter(wo => calculateAge(wo.lastActionDate) > 1 && calculateAge(wo.lastActionDate) <= 3).length;
        const old = filteredData.filter(wo => calculateAge(wo.lastActionDate) > 3 && calculateAge(wo.lastActionDate) <= 7).length;
        const veryOld = filteredData.filter(wo => calculateAge(wo.lastActionDate) > 7).length;
        
        return [
          {
            title: "Average Age",
            key: "avgAge",
            color: "#495057",
            percentage: "All WOs",
            value: `${avgAge} days`,
            icon: "📅"
          },
          {
            title: "Fresh (≤1 day)",
            key: "fresh",
            color: "#198754",
            percentage: totalPending > 0 ? `${Math.round((fresh / totalPending) * 100)}` : "0",
            value: `${fresh}`,
            icon: "🆕"
          },
          {
            title: "Recent (2-3 days)",
            key: "recent",
            color: "#FFC107",
            percentage: totalPending > 0 ? `${Math.round((recent / totalPending) * 100)}` : "0",
            value: `${recent}`,
            icon: "📋"
          },
          {
            title: "Old (4-7 days)",
            key: "old",
            color: "#FD7E14",
            percentage: totalPending > 0 ? `${Math.round((old / totalPending) * 100)}` : "0",
            value: `${old}`,
            icon: "⏰"
          },
          {
            title: "Very Old (>7 days)",
            key: "veryOld",
            color: "#DC3545",
            percentage: totalPending > 0 ? `${Math.round((veryOld / totalPending) * 100)}` : "0",
            value: `${veryOld}`,
            icon: "🚨"
          }
        ];

      default:
        return [];
    }
  };

  const exportFilteredData = () => {
    const csvContent = "data:text/csv;charset=utf-8," + 
      "WO Number,Title,Site,Project,Contractor,Value,Status,Last Action By,Last Action Date,Age\n" +
      filteredData.map(wo => 
        `${wo.workOrderNumber},"${wo.workOrderTitle}",${wo.siteName},${wo.projectName},${wo.contractorName},${wo.grossValue},${wo.status},${wo.lastActionBy},${wo.lastActionDate},${calculateAge(wo.lastActionDate)}`
      ).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `pending_work_orders_${dayjs().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('Data exported successfully');
  };

  const getStatusColor = (status: ApprovalStatus) => {
    switch (status) {
      case ApprovalStatus.Pending:
        return "orange";
      case ApprovalStatus.Checked:
        return "blue";
      case ApprovalStatus.Verified:
        return "cyan";
      case ApprovalStatus.Approved:
        return "green";
      case ApprovalStatus.ReturnedForCorrection:
        return "red";
      default:
        return "default";
    }
  };

  const getRoleDisplayName = (role: UserRole) => {
    switch (role) {
      case UserRole.Checker:
        return "Checker";
      case UserRole.Verifier:
        return "Verifier";
      case UserRole.Approver:
        return "Approver";
      default:
        return "";
    }
  };

  const getNextActionBy = (status: ApprovalStatus) => {
    switch (status) {
      case ApprovalStatus.SubmittedForApproval:
        return "Checker";
      case ApprovalStatus.Checked:
        return "Verifier";
      case ApprovalStatus.Verified:
        return "Approver";
      default:
        return "";
    }
  };

  const calculateAge = (lastActionDate: string) => {
    return dayjs().diff(dayjs(lastActionDate), 'days');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleViewWorkOrder = (record: PendingWorkOrderData) => {
    navigate(`/work-order-view/${record.id}`);
  };

  const handleApprove = (record: PendingWorkOrderData) => {
    setSelectedWorkOrder(record);
    setIsApprovalModalVisible(true);
  };

  const handleReturn = (record: PendingWorkOrderData) => {
    setSelectedWorkOrder(record);
    setIsReturnModalVisible(true);
  };

  const processApproval = async (values: any) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const nextStatus = currentUserRole === UserRole.Checker ? ApprovalStatus.Checked :
                        currentUserRole === UserRole.Verifier ? ApprovalStatus.Verified :
                        ApprovalStatus.Approved;
      
      message.success(`Work Order approved and moved to ${getNextActionBy(nextStatus)}`);
      setIsApprovalModalVisible(false);
      approvalForm.resetFields();
      loadPendingWorkOrders();
    } catch (error) {
      message.error("Failed to approve work order");
    }
  };

  const processReturn = async (values: any) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      message.success("Work Order returned for correction");
      setIsReturnModalVisible(false);
      returnForm.resetFields();
      loadPendingWorkOrders();
    } catch (error) {
      message.error("Failed to return work order");
    }
  };

  const columns = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 200,
      render: (text: string, record: PendingWorkOrderData) => (
         <span
              className="inlineTableCellLink"
              id={`woStatus-${record?.id}`}
              onClick={() => handleViewWorkOrder(record)}
            >
              {text}
            </span>
        // <Button 
        //   type="link" 
        //   onClick={() => handleViewWorkOrder(record)}
        //   style={{ padding: 0, height: 'auto' }}
        // >
        //   {text}
        // </Button>
      ),
    },
    {
      title: "Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Site / Project",
      key: "siteProject",
      width: 180,
      render: (_:any, record: PendingWorkOrderData) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.siteName}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.projectName}</div>
        </div>
      ),
    },
    {
      title: "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 150,
      ellipsis: true,
    },
    {
      title: "WO Value",
      dataIndex: "grossValue",
      key: "grossValue",
      width: 120,
      render: (value: number) => (
        <span style={{ fontWeight: 500 }}>{formatCurrency(value)}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 180,
      render: (status: ApprovalStatus) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "Last Action By",
      dataIndex: "lastActionBy",
      key: "lastActionBy",
      width: 120,
    },
    {
      title: "Last Action Date",
      dataIndex: "lastActionDate",
      key: "lastActionDate",
      width: 120,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: "Next Action By",
      key: "nextActionBy",
      width: 120,
      render: (_:any, record: PendingWorkOrderData) => (
        <span style={{ fontWeight: 500, color: '#1890ff' }}>
          {getNextActionBy(record.status)}
        </span>
      ),
    },
    {
      title: "Age (Days)",
      key: "age",
      width: 100,
      render: (_:any, record: PendingWorkOrderData) => {
        const age = calculateAge(record.lastActionDate);
        return (
          <span style={{ 
            color: age > 3 ? '#ff4d4f' : age > 1 ? '#faad14' : '#52c41a',
            fontWeight: 500 
          }}>
            {age}
          </span>
        );
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      fixed: 'right' as const,
      render: (_:any, record: PendingWorkOrderData) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              type="primary" 
              ghost 
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => handleViewWorkOrder(record)}
            />
          </Tooltip>
          <Tooltip title="Approve">
            <Button 
              type="primary" 
              size="small" 
              icon={<CheckOutlined />}
              onClick={() => handleApprove(record)}
            />
          </Tooltip>
          <Tooltip title="Return">
            <Button 
              danger 
              size="small" 
              icon={<CloseOutlined />}
              onClick={() => handleReturn(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
    <TransactionFlowDefaultTemplate
      moduleKey="pending-work-order-approvals"
      title="Pending Work Order Approvals"
      columns={columns}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <WorkOrderAside updateSm={setSm} sm={sm} />
        </div>
      }
      extra={{
        tableScrollX: 1500,
        dateRange: false,
        excelExport: true,
        dataFilterTootip: "Filtering based on Work Order last action date",
      }}
      // widget={{
      //   isActive: true,
      //   compactGrid: true,
      //   kpiFilter: {
      //     isActive: false,
      //     options: kpiFilterOptions,
      //     selectedFilter: selectedKPIFilter,
      //     onFilterChange: handleKPIFilterChange,
      //   },
      //   list: getWidgetsByFilter(selectedKPIFilter),
      // }}
      topRightJSX={
        <Space>
          <Badge count={getActiveFilterCount()} size="small">
            <Button 
              type="primary" 
              icon={<FilterOutlined />} 
              onClick={() => setFilterDrawerVisible(true)}
            >
              Advanced Filters
            </Button>
          </Badge>
          {/* <Button 
            type="primary" 
            icon={<ReloadOutlined />}
            onClick={loadPendingWorkOrders}
            loading={loading}
            size="large"
          >
            Refresh
          </Button> */}
        </Space>
      }
    />

    {/* Advanced Filter Drawer */}
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>
            <FilterOutlined style={{ marginRight: 8 }} />
            Filters
          </span>
          <Badge count={getActiveFilterCount()} showZero={false} />
        </div>
      }
      placement="right"
      width={420}
      open={filterDrawerVisible}
      onClose={() => setFilterDrawerVisible(false)}
      extra={
        <Space>
          <Button 
            icon={<SaveOutlined />} 
            onClick={saveCurrentFilter}
            disabled={getActiveFilterCount() === 0}
          >
            Save Filter
          </Button>
          <Button 
            icon={<ClearOutlined />} 
            onClick={clearFilters}
            disabled={getActiveFilterCount() === 0}
          >
            Reset
          </Button>
        </Space>
      }
    >
      <div style={{ padding: '0 0 16px 0' }}>
        {/* Saved Filters */}
        {savedFilters.length > 0 && (
          <>
            <div style={{ marginBottom: 24 }}>
              <Title level={5} style={{ marginBottom: 12 }}>Saved Filters</Title>
              <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                {savedFilters.map(savedFilter => (
                  <div key={savedFilter.id} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '4px 0',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <Button 
                      type="link" 
                      size="small"
                      onClick={() => applySavedFilter(savedFilter)}
                      style={{ padding: 0, height: 'auto', textAlign: 'left' }}
                    >
                      {savedFilter.name}
                    </Button>
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<DeleteOutlined />}
                      onClick={() => deleteSavedFilter(savedFilter.id)}
                      danger
                    />
                  </div>
                ))}
              </div>
            </div>
            <Divider />
          </>
        )}

        {/* Filter Fields */}
        <div className="filter-section">
          {/* <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Global Search</Text>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by WO Number, Title, Contractor, Site, Project..."
              value={filters.globalSearch}
              onChange={(e) => handleFilterChange('globalSearch', e.target.value)}
              allowClear
            />
          </div> */}

          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Site</Text>
            <Select
              placeholder="Select Site"
              value={filters.site}
              onChange={(value) => handleFilterChange('site', value)}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {mockSiteOptions.map(site => (
                <Option key={site.value} value={site.value}>{site.label}</Option>
              ))}
            </Select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Project</Text>
            <Select
              placeholder="Select Project"
              value={filters.project}
              onChange={(value) => handleFilterChange('project', value)}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {mockProjectOptions.map(project => (
                <Option key={project.value} value={project.value}>{project.label}</Option>
              ))}
            </Select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Status</Text>
            <Select
              placeholder="Select Status"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value={ApprovalStatus.SubmittedForApproval}>Submitted for Approval</Option>
              <Option value={ApprovalStatus.Checked}>Checked</Option>
              <Option value={ApprovalStatus.Verified}>Verified</Option>
              <Option value={ApprovalStatus.Approved}>Approved</Option>
              <Option value={ApprovalStatus.ReturnedForCorrection}>Returned for Correction</Option>
            </Select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Contract Type</Text>
            <Select
              placeholder="Select Contract Type"
              value={filters.contractType}
              onChange={(value) => handleFilterChange('contractType', value)}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {mockContractTypeOptions.map(type => (
                <Option key={type.value} value={type.value}>{type.label}</Option>
              ))}
            </Select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Work Order Owner</Text>
            <Input
              placeholder="Enter WO Owner name"
              value={filters.woOwner}
              onChange={(e) => handleFilterChange('woOwner', e.target.value)}
              allowClear
            />
          </div>

          {/* <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Value Range (₹)</Text>
            <Row gutter={8}>
              <Col span={12}>
                <Input
                  placeholder="Min Value"
                  value={filters.valueRange.min}
                  onChange={(e) => handleFilterChange('valueRange', { 
                    ...filters.valueRange, 
                    min: e.target.value 
                  })}
                  type="number"
                />
              </Col>
              <Col span={12}>
                <Input
                  placeholder="Max Value"
                  value={filters.valueRange.max}
                  onChange={(e) => handleFilterChange('valueRange', { 
                    ...filters.valueRange, 
                    max: e.target.value 
                  })}
                  type="number"
                />
              </Col>
            </Row>
          </div> */}

          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Date Range</Text>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange('dateRange', dates)}
              style={{ width: '100%' }}
              placeholder={['Start Date', 'End Date']}
              format="DD/MM/YYYY"
            />
          </div>

          {/* <Divider /> */}

          {/* Apply & Export Actions */}
          {/* <div style={{ marginTop: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button 
                type="primary" 
                block
                onClick={() => setFilterDrawerVisible(false)}
                disabled={getActiveFilterCount() === 0}
              >
                Apply Filters ({filteredData.length} results)
              </Button>
              <Button 
                block
                icon={<ExportOutlined />}
                onClick={() => {
                  exportFilteredData();
                  setFilterDrawerVisible(false);
                }}
                disabled={filteredData.length === 0}
              >
                Export Filtered Data
              </Button>
            </Space>
          </div> */}
        </div>
      </div>
    </Drawer>

    {/* Approval Modal */}
    <Modal
      title="Approve Work Order"
      open={isApprovalModalVisible}
      onCancel={() => setIsApprovalModalVisible(false)}
      footer={null}
    >
      <Form
        form={approvalForm}
        layout="vertical"
        onFinish={processApproval}
      >
        <p>
          <strong>Work Order:</strong> {selectedWorkOrder?.workOrderNumber}<br />
          <strong>Title:</strong> {selectedWorkOrder?.workOrderTitle}<br />
          <strong>Value:</strong> {selectedWorkOrder && formatCurrency(selectedWorkOrder.grossValue)}
        </p>
        
        <Form.Item
          name="comments"
          label="Approval Comments (Optional)"
        >
          <TextArea 
            rows={4} 
            placeholder="Add any comments or observations..."
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={() => setIsApprovalModalVisible(false)}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" icon={<CheckOutlined />}>
              Approve & Forward
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>

    {/* Return Modal */}
    <Modal
      title="Return Work Order for Correction"
      open={isReturnModalVisible}
      onCancel={() => setIsReturnModalVisible(false)}
      footer={null}
    >
      <Form
        form={returnForm}
        layout="vertical"
        onFinish={processReturn}
      >
        <p>
          <strong>Work Order:</strong> {selectedWorkOrder?.workOrderNumber}<br />
          <strong>Title:</strong> {selectedWorkOrder?.workOrderTitle}
        </p>
        
        <Form.Item
          name="returnComments"
          label="Return Comments"
          rules={[
            { required: true, message: 'Please provide reasons for returning this work order' },
            { min: 10, message: 'Comments must be at least 10 characters long' }
          ]}
        >
          <TextArea 
            rows={4} 
            placeholder="Please specify the issues that need to be corrected..."
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={() => setIsReturnModalVisible(false)}>
              Cancel
            </Button>
            <Button type="primary" danger htmlType="submit" icon={<CloseOutlined />}>
              Return for Correction
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>

</>
    
  );
};

export default PendingWorkOrderApprovals;